/**
 * useElementBounds (web) — a cross-target measurement seam.
 *
 * Returns a `ref` to attach to an element and a `measure()` that yields its
 * screen-space rect. The shared editor uses this instead of calling
 * `getBoundingClientRect` directly (e.g. to position the PageNode ⋯-menu), so
 * the same code runs on native, where `useElementBounds.native.ts` measures a
 * RN view via `measureInWindow`.
 *
 * On web `measure()` is synchronous (returns the rect or null); the native
 * variant returns a Promise, so callers `await` it — awaiting a non-Promise is
 * a no-op, keeping call sites identical across platforms.
 */
import { useRef } from "react";

export interface ElementBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly width: number;
  readonly height: number;
}

export interface MeasuredRef<T extends HTMLElement = HTMLElement> {
  readonly ref: React.RefObject<T | null>;
  readonly measure: () => ElementBounds | null;
}

export function useElementBounds<
  T extends HTMLElement = HTMLElement,
>(): MeasuredRef<T> {
  const ref = useRef<T | null>(null);
  const measure = (): ElementBounds | null => {
    const el = ref.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return {
      left: r.left,
      top: r.top,
      right: r.right,
      bottom: r.bottom,
      width: r.width,
      height: r.height,
    };
  };
  return { ref, measure };
}
